import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Stack,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import EventEditor from "../components/EventEditor";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { supabase } from '../utils/supabaseClient';
import { useNavigate } from "react-router-dom";
import mockData from './mockData.json'
import mockDataDay from './mockDataDay.json'


interface DayEntry {
  day: string;
  hours: string;
}

interface EventEntry {
  id: string;
  title: string;
  day: string;
  startTime: string;
  endTime?: string;
  cancelled: boolean;
  isRecurring?: string;
  eventDate?: string;
  link?: string;
  recurringID?: string | null;
  isOverride?: boolean;
}


const Hours: React.FC = () => {
  const now = new Date();
  const todayIndex = (now.getDay() + 6) % 7;

  const navigate = useNavigate();

  const [hoursData, setHoursData] = useState<DayEntry[]>([]);
  const [eventsData, setEventsData] = useState<EventEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [hoursOverrides, setHoursOverrides] = useState<{
  [dateStr: string]: {
    closedEarly: boolean;
    closeTime?: string;
    note?: string;
  };
}>({});

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];


  useEffect(() => {
    const fetchData = async () => {
      try {

const [hoursRes, overridesRes, eventsResult] = await Promise.all([
  fetch("/data/hours.json", { cache: "no-store" }),
  fetch("/data/overrides.json", { cache: "no-store" }),

  // 🔥 Supabase instead of events.json
  supabase
    .from("events")
    .select("*")
    .order("event_date", { ascending: true })
]);

const hoursJson = await hoursRes.json();
const overridesJson = await overridesRes.json();

// Supabase response
const { data: eventsDataDB, error: eventsError } = eventsResult;

if (eventsError) {
  console.error("❌ Supabase fetch error:", eventsError.message);
}


        const parsedHours: DayEntry[] = hoursJson.records.map((r: any) => {
          const fields = r.fields;
          const isClosed = !!fields.isClosed;
          const open = fields.Open;
          const close = fields.Close;

          let hours: string;
          if (isClosed) {
            hours = "CLOSED";
          } else {
            hours = `${open} – ${close}`;
          }

          return {
            day: fields.Day,
            hours,
          };
        });

        const parsedEvents: EventEntry[] = (eventsDataDB || []).map((r: any) => ({
          id: r.id,
          title: r.title,
          day: r.day,
          startTime: r.start_time,
          endTime: r.end_time || undefined,
          cancelled: r.cancelled ?? false,
          isRecurring: r.is_recurring || "",
          eventDate: r.event_date || "",
          link: r.link || "",
          recurringID: r.recurring_id || null,
          isOverride: r.is_override ?? false,
        }));

        const parsedOverrides = overridesJson.records.reduce((acc: any, r: any) => {
        const fields = r.fields;
        const dateStr = fields.OverrideDate?.trim();
        if (!dateStr) return acc;

        acc[dateStr] = {
          closedEarly: fields.ClosedEarly || false,
          closeTime: fields.EarlyCloseTime || "",
          note: fields.Note || ""
        };
        return acc;
      }, {});

        const orderedHours = parsedHours.sort(
          (a, b) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day)
        );

        setHoursData(orderedHours);
        setEventsData(parsedEvents);
        setHoursOverrides(parsedOverrides);

      } catch (err) {
        console.error("Failed to fetch Airtable data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const parse12HourTime = (timeStr: string): Date | null => {
    if (!timeStr) return null;
    const match = timeStr.match(/^(\d{1,2}):?(\d{2})?(AM|PM)$/i);
    if (!match) return null;

    let [_, hourStr, minuteStr = "00", period] = match;
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (period.toUpperCase() === "AM" && hour === 12) hour = 0;

    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    return date;
  };

  const formatDateStr = (monday: Date, index: number): string => {
  const date = new Date(monday);
  date.setDate(monday.getDate() + index);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }); 
};

const formatDateForCompare = (monday: Date, index: number): string => {
  const d = new Date(monday);
  d.setDate(monday.getDate() + index);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

  const formatTime12Hour = (timeStr: string): string => {
    const date = parse12HourTime(timeStr);
    if (!date) return "Invalid time";
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const isEventLive = (event: EventEntry) => {
    if (event.cancelled) return false;
    const eventDayIndex = hoursData.findIndex((d) => d.day === event.day);
    if (eventDayIndex !== todayIndex) return false;

    const start = parse12HourTime(event.startTime);
    if (!start) return false;

    let end: Date;
    if (event.endTime) {
      const parsedEnd = parse12HourTime(event.endTime);
      end = parsedEnd ?? new Date(start.getTime() + 4 * 60 * 60 * 1000);
    } else {
      end = new Date(start.getTime() + 4 * 60 * 60 * 1000);
    }

    return now >= start && now <= end;
  };

const getEventDateFromDay = (event: EventEntry): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

 
  if (event.eventDate) {
    const [mm, dd, yyyy] = event.eventDate.split("/").map(Number);
    if (mm && dd && yyyy) {
      const eventDate = new Date(yyyy, mm - 1, dd);
      eventDate.setHours(0, 0, 0, 0);

      const formatted = eventDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

      const isToday = eventDate.getTime() === today.getTime();
      return isToday && weekOffset === 0 ? `Today – ${formatted}` : formatted;
    }
  }

  const eventDay = event.day;
  const eventIndex = daysOfWeek.indexOf(eventDay);
  const todayIndex = (today.getDay() + 6) % 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - todayIndex + weekOffset * 7);
  const eventDate = new Date(monday);
  eventDate.setDate(monday.getDate() + eventIndex);
  eventDate.setHours(0, 0, 0, 0);

  const formatted = eventDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  const isToday = eventDate.getTime() === today.getTime();
  return isToday && weekOffset === 0 ? `Today – ${formatted}` : formatted;
};


  const isPastEvent = (eventDay: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const eventIndex = daysOfWeek.indexOf(eventDay);
    const todayIndex = (today.getDay() + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - todayIndex);
    monday.setHours(0, 0, 0, 0);

    const eventDate = new Date(monday);
    eventDate.setDate(monday.getDate() + eventIndex);
    eventDate.setHours(0, 0, 0, 0);

    return eventDate.getTime() < today.getTime();
  };

  const filterEventsForDay = (cardDay: string): EventEntry[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todayIndex = (today.getDay() + 6) % 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - todayIndex + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(0, 0, 0, 0);

  const cardDayIndex = daysOfWeek.indexOf(cardDay);
  const cardDateStr = formatDateForCompare(monday, cardDayIndex);

  return eventsData.flatMap((e) => {
    const isRecurring = e.isRecurring?.toLowerCase() === "yes";
    const isOneTime = !isRecurring && e.eventDate;

    const normalizeDate = (dateStr?: string | null): string => {
  if (!dateStr) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-");
    return `${m}/${d}/${y}`;
  }

  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const [m, d, y] = dateStr.split("/");
    return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
  }

  return dateStr;
};

    // ✅ ONE-TIME EVENTS (includes overrides)
if (isOneTime) {
  const normalized = normalizeDate(e.eventDate);

  if (!normalized) return [];

  const [mm, dd, yyyy] = normalized.split("/").map(Number);
  const actualDate = new Date(yyyy, mm - 1, dd);
  actualDate.setHours(0, 0, 0, 0);

  if (actualDate >= monday && actualDate <= sunday) {
    const actualIndex = (actualDate.getDay() + 6) % 7;
    if (actualIndex === cardDayIndex) {
      return [e];
    }
  }

  return [];
}

    // ✅ RECURRING EVENTS
    if (isRecurring && e.day === cardDay) {

      // 🔥 KEEP YOUR EXISTING OVERRIDE LOGIC
      const overrideExists = eventsData.some((other) => {
        return (
          normalizeDate(other.eventDate) === cardDateStr &&
          other.day === cardDay &&
          other.isRecurring?.toLowerCase() !== "yes" &&
          other.recurringID &&
          e.recurringID &&
          other.recurringID === e.recurringID &&
          other.isOverride === true
        );
      });

      // ❌ Override replaces recurring → hide it
      if (overrideExists) {
        return [];
      }

      // 🔴 Cancel ALL weeks
      if (e.cancelled && !e.eventDate) {
        return [{ ...e, cancelled: true }];
      }

      // 🔴 Cancel ONLY this date
    if (
        e.cancelled &&
        normalizeDate(e.eventDate) === cardDateStr
      ) {
        return [{ ...e, cancelled: true }];
      }

      // 🟢 Normal recurring
      return [{ ...e, cancelled: false }];
    }

    return [];
  });
};

  const displayData = loading
    ? daysOfWeek.map((day) => ({ day, hours: "Loading..." }))
    : hoursData;

  return (
    
    <Box sx={{ bgcolor: "#1E1E2F", py: 6, userSelect: "none" }}>
      <Container maxWidth="lg">
        <Paper
          elevation={6}
          sx={{
            bgcolor: "#242435",
            p: { xs: 3, md: 5 },
            borderRadius: 3,
          }}
        >
         <Typography
  variant="h4"
  align="center"
  gutterBottom
  onDoubleClick={(e) => {
    if (e.shiftKey) {
      navigate("/admin");
    }
  }}
  sx={{
    color: "#e0e0e0",
    fontWeight: 600,
    mb: 4,
    cursor: "pointer",
  }}
>
  Hours & Events
</Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          {weekOffset === 1 && (
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArrowBackIcon fontSize="small" />
                  <span style={{ fontSize: '0.75rem' }}>This Week</span>
                </Box>
              }
              onClick={() => setWeekOffset(0)}
              variant="outlined"
              sx={{
                color: "#89C7C8",
                borderColor: "#89C7C8",
                fontWeight: 600,
                px: 1.5,
                height: 28,
              }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {weekOffset === 0 && (
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span style={{ fontSize: '0.75rem' }}>Next Week</span>
                  <ArrowForwardIcon fontSize="small" />
                </Box>
              }
              onClick={() => setWeekOffset(1)}
              variant="outlined"
              sx={{
                color: "#89C7C8",
                borderColor: "#89C7C8",
                fontWeight: 600,
                px: 1.5,
                height: 28,
              }}
            />
          )}
        </Box>
          <Grid container spacing={2} justifyContent="center">
            {displayData.map(({ day, hours }, index) => {
              const dayEvents = filterEventsForDay(day);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const todayIndex = (today.getDay() + 6) % 7;

              const monday = new Date(today);
              monday.setDate(today.getDate() - todayIndex + weekOffset * 7);
              monday.setHours(0, 0, 0, 0);


              return (
                <Grid item xs={12} sm={6} md={4} key={day}>
                  <Box
                    sx={{
                      border: weekOffset === 0 && todayIndex === index
                        ? "2px solid #89C7C8"
                        : "1px solid #2D2D42",
                      bgcolor: weekOffset === 0 && todayIndex === index
                        ? "#333356"
                        : "#2D2D42",
                      borderRadius: 2,
                      p: 2,
                      textAlign: "center",
                      transition: "background 0.3s, transform 0.3s",
                      "&:hover": {
                        bgcolor: "#3A3A5A",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "#FFFFFF", fontWeight: 600, mb: 1 }}
                    >
                      {day}
                    </Typography>
                    {(() => {
  const cardDateStr = formatDateStr(monday, index);
  const override = hoursOverrides[cardDateStr];
  const regularHours =
    hoursData.find((d) => d.day === day)?.hours || "CLOSED";

  // 🇺🇸 Holiday Override
  const isJuly4th2026 = cardDateStr === "07/04/2026";

  return (
    <>
      {isJuly4th2026 ? (
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 2,
            background:
              "linear-gradient(135deg,#0D47A1 0%, #1565C0 45%, #B71C1C 100%)",
            border: "2px solid #FFD54F",
          }}
        >
          <Typography
            sx={{
              fontSize: "2rem",
              textAlign: "center",
              mb: 0.5,
            }}
          >
            🎆
          </Typography>

          <Chip
            label="CLOSED"
            color="error"
            sx={{
              width: "100%",
              fontWeight: 700,
              fontSize: "1rem",
              mb: 1.5,
            }}
          />

          <Typography
            sx={{
              color: "#FFF",
              fontWeight: 700,
              fontSize: "1.05rem",
              textAlign: "center",
            }}
          >
            Happy 4th of July!
          </Typography>

          <Typography
            sx={{
              color: "#EAEAEA",
              fontSize: ".85rem",
              textAlign: "center",
              mt: 0.5,
            }}
          >
            We are closed on July 4th in observance of
            <br />
            Independence Day.
          </Typography>

          <Typography
            sx={{
              color: "#FFD54F",
              fontWeight: 600,
              textAlign: "center",
              mt: 1,
            }}
          >
            Have a safe and wonderful holiday! 🎇
          </Typography>
        </Box>
      ) : (
        <>
          <Typography
            variant="body2"
            sx={{
              color: regularHours === "CLOSED" ? "#FF7373" : "#C0C0C0",
              fontSize: "0.95rem",
              fontWeight: 500,
            }}
          >
            {regularHours}
          </Typography>

          {override?.closedEarly && (
            <Stack spacing={1} mt={1}>
              <Chip
                label={`Closing Early Today: ${
                  override.closeTime || "Unknown Time"
                }`}
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />

              {override.note && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#AAA",
                    fontStyle: "italic",
                  }}
                >
                  {override.note}
                </Typography>
              )}
            </Stack>
          )}
        </>
      )}
    </>
  );
})()}


                    {dayEvents.length > 0 && (
                      <Stack spacing={1} mt={2}>
                        {dayEvents.map((event) => (
                          <Box
                            key={event.id}
                            sx={{
                              bgcolor: "#1E1E2F",
                              borderRadius: 1,
                              p: 1,
                              opacity: event.cancelled ? 0.5 : 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#AAAAAA",
                                textAlign: "center",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              {getEventDateFromDay(event)}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#FFFFFF", fontWeight: 500 }}
                              >
                                {event.title}
                              </Typography>
                              {event.link && event.link.trim() !== "" && (
                                <IconButton
                                  size="small"
                                  href={event.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: "#89C7C8", ml: 1 }}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>

                            <Typography
                              variant="caption"
                              sx={{ color: "#C0C0C0" }}
                            >
                              {event.endTime && event.endTime.trim() !== ""
                                ? `${formatTime12Hour(event.startTime)} – ${formatTime12Hour(event.endTime)}`
                                : `Starts at ${formatTime12Hour(event.startTime)}`}
                            </Typography>
                          <Stack direction="row" justifyContent="center" spacing={1} mt={0.5}>
                            {event.cancelled && (
                              <Chip
                                label="Cancelled"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                            {!event.cancelled && weekOffset === 0 && isEventLive(event) && (
                              <Chip
                                label="LIVE"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                            {!event.cancelled && weekOffset === 0 && !isEventLive(event) && isPastEvent(event.day) && (
                              <Chip
                                label="Ended"
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  bgcolor: "#444",
                                  color: "#aaa",
                                }}
                              />
                            )}
                          </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Hours;
